'use client';

import { Slot, Slottable } from '@radix-ui/react-slot';
import type { EmblaCarouselType, EmblaOptionsType, EmblaPluginType } from 'embla-carousel';
import Autoplay, { type AutoplayOptionsType } from 'embla-carousel-autoplay';
import Fade from 'embla-carousel-fade';
import useEmblaCarousel from 'embla-carousel-react';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { cn } from '../../lib/utils';
import { useStableValue } from '../../hooks/use-stable-value';
import { Flex } from '../atoms';
import { Button } from '../button/button';
import { IconButton } from '../icon-button/icon-button';
import { Text } from '../typography';
import { ChevronLeft, ChevronRight } from '../../icons';
import { type CarouselPresetName, carouselPresets } from './carousel-presets';
import {
  CarouselDynamicContext,
  CarouselStaticContext,
  useCarouselDynamic,
  useCarouselStatic,
} from './carousel-context';

export {
  carouselPresets,
  type CarouselPresetConfig,
  type CarouselPresetName,
} from './carousel-presets';
export { useCarouselDynamic, useCarouselStatic } from './carousel-context';

// ---------------------------------------------------------------------------
// Carousel (Root)
// ---------------------------------------------------------------------------

export interface CarouselProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Preset configuration name */
  preset?: CarouselPresetName;
  /** Embla options — merged on top of preset defaults */
  opts?: EmblaOptionsType;
  /**
   * Additional Embla plugins. **Memoise the array** — `Plugin()` returns a
   * fresh instance each call, so an inline `plugins={[Autoplay()]}` will
   * re-initialise Embla on every render. Prefer the `autoplay` / `fade`
   * convenience props for the common cases.
   */
  plugins?: EmblaPluginType[];
  /** Enable autoplay (true for defaults, or pass an options object) */
  autoplay?: boolean | AutoplayOptionsType;
  /** Enable fade transition plugin */
  fade?: boolean;
  /** Scroll axis */
  orientation?: 'horizontal' | 'vertical';
  /**
   * Arrow / keyboard nav strategy.
   * - `'page'` (default): advance by exactly one viewport per click, regardless
   *   of snap layout — keeps card carousels visually consistent when the tail
   *   page is shorter than a viewport. Uses Embla's `internalEngine()` (pinned
   *   to the major version we depend on).
   * - `'snap'`: use Embla's public `scrollNext` / `scrollPrev` (snap-aware).
   *   Choose this if you don't want any internal-engine coupling.
   */
  scrollMode?: 'page' | 'snap';
}

const Carousel = forwardRef<HTMLDivElement, CarouselProps>(
  (
    {
      preset,
      opts,
      plugins: userPlugins,
      autoplay,
      fade,
      orientation = 'horizontal',
      scrollMode = 'page',
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const stableOpts = useStableValue(opts);
    const stableAutoplay = useStableValue(autoplay);

    const presetConfig = useMemo(() => (preset ? carouselPresets[preset] : undefined), [preset]);

    const mergedOpts = useMemo<EmblaOptionsType>(
      () => ({
        ...presetConfig?.opts,
        ...stableOpts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      }),
      [presetConfig?.opts, stableOpts, orientation],
    );

    const allPlugins = useMemo(() => {
      const list: EmblaPluginType[] = userPlugins ? [...userPlugins] : [];
      if (stableAutoplay) {
        const autoplayDefaults: AutoplayOptionsType = {
          stopOnInteraction: false,
          stopOnMouseEnter: true,
        };
        list.push(
          Autoplay(
            typeof stableAutoplay === 'object'
              ? { ...autoplayDefaults, ...stableAutoplay }
              : autoplayDefaults,
          ),
        );
      }
      if (fade) {
        list.push(Fade());
      }
      return list;
    }, [stableAutoplay, fade, userPlugins]);

    const [emblaRef, emblaApi] = useEmblaCarousel(mergedOpts, allPlugins);

    const [scrollState, setScrollState] = useState({
      canScrollPrev: false,
      canScrollNext: false,
      selectedIndex: 0,
    });
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

    const onSelect = useCallback(
      (api: EmblaCarouselType) => {
        setScrollState((prev) => {
          let canScrollPrev: boolean;
          let canScrollNext: boolean;
          if (scrollMode === 'snap') {
            canScrollPrev = api.canScrollPrev();
            canScrollNext = api.canScrollNext();
          } else {
            // Page-mode reachability: are there pixels left in either direction?
            // Embla's snap-based canScrollPrev/Next reports false at the last snap
            // even when the tail page is shorter than a viewport and pixel room
            // remains. Mirror the clamp logic in scrollByDirection.
            const engine = api.internalEngine();
            if (engine.options.loop) {
              canScrollPrev = true;
              canScrollNext = true;
            } else {
              const isVertical = engine.axis.scroll === 'y';
              const viewportSize = isVertical
                ? engine.containerRect.height
                : engine.containerRect.width;
              const currentTarget = engine.target.get();
              canScrollPrev =
                engine.limit.constrain(currentTarget + viewportSize) !== currentTarget;
              canScrollNext =
                engine.limit.constrain(currentTarget - viewportSize) !== currentTarget;
            }
          }
          const selectedIndex = api.selectedScrollSnap();
          if (
            prev.canScrollPrev === canScrollPrev &&
            prev.canScrollNext === canScrollNext &&
            prev.selectedIndex === selectedIndex
          ) {
            return prev;
          }
          return { canScrollPrev, canScrollNext, selectedIndex };
        });
      },
      [scrollMode],
    );

    // 'page' mode: free-scroll by exactly one viewport per click regardless of
    // snap layout (lands between snaps when the tail page is short). Requires
    // Embla's `internalEngine()` — an explicit, version-coupled escape hatch
    // documented on `scrollMode`.
    const scrollByDirection = useCallback(
      (direction: 1 | -1) => {
        if (!emblaApi) return;
        if (scrollMode === 'snap') {
          if (direction === -1) emblaApi.scrollPrev();
          else emblaApi.scrollNext();
          return;
        }
        const engine = emblaApi.internalEngine();
        const isLoop = engine.options.loop;
        const isVertical = engine.axis.scroll === 'y';
        const viewportSize = isVertical ? engine.containerRect.height : engine.containerRect.width;
        // Embla's scroll location decreases as the carousel advances forward,
        // so a forward step is a negative pixel distance.
        const desiredDistance = -direction * viewportSize;
        let distance = desiredDistance;
        if (!isLoop) {
          const currentTarget = engine.target.get();
          const clampedTarget = engine.limit.constrain(currentTarget + desiredDistance);
          distance = clampedTarget - currentTarget;
        }
        if (distance !== 0) {
          engine.scrollTo.distance(distance, false);
        }
        // Sync canScroll state to the new target immediately. Embla's settle
        // event lags here (no-op clamps don't fire settle; rapid clicks outpace
        // the animation), so relying on it alone leaves the button one click
        // behind at boundaries.
        onSelect(emblaApi);
      },
      [emblaApi, scrollMode, onSelect],
    );
    const scrollPrev = useCallback(() => scrollByDirection(-1), [scrollByDirection]);
    const scrollNext = useCallback(() => scrollByDirection(1), [scrollByDirection]);
    const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

    const onInit = useCallback((api: EmblaCarouselType) => {
      setScrollSnaps((prev) => {
        const next = api.scrollSnapList();
        if (prev.length === next.length && prev.every((v, i) => v === next[i])) {
          return prev;
        }
        return next;
      });
    }, []);

    useEffect(() => {
      if (!emblaApi) return;
      onInit(emblaApi);
      onSelect(emblaApi);
      emblaApi.on('select', onSelect);
      // 'settle' fires after every scroll motion completes, including non-snap
      // page scrolls — `select` alone misses those, so canScrollPrev/Next would
      // not update after a partial page advance lands between snaps.
      emblaApi.on('settle', onSelect);
      emblaApi.on('reInit', onInit);
      emblaApi.on('reInit', onSelect);
      return () => {
        emblaApi.off('select', onSelect);
        emblaApi.off('settle', onSelect);
        emblaApi.off('reInit', onInit);
        emblaApi.off('reInit', onSelect);
      };
    }, [emblaApi, onInit, onSelect]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        // Only handle keys when the carousel root itself is focused — let
        // arrow keys flow through to focused descendants (inputs, sliders, etc.)
        if (e.target !== e.currentTarget) return;
        const isHorizontal = orientation === 'horizontal';
        if ((isHorizontal && e.key === 'ArrowLeft') || (!isHorizontal && e.key === 'ArrowUp')) {
          e.preventDefault();
          scrollPrev();
        } else if (
          (isHorizontal && e.key === 'ArrowRight') ||
          (!isHorizontal && e.key === 'ArrowDown')
        ) {
          e.preventDefault();
          scrollNext();
        }
      },
      [orientation, scrollPrev, scrollNext],
    );

    const staticValue = useMemo(
      () => ({ emblaRef, orientation, presetConfig }),
      [emblaRef, orientation, presetConfig],
    );

    const dynamicValue = useMemo(
      () => ({
        emblaApi,
        ...scrollState,
        scrollSnaps,
        scrollPrev,
        scrollNext,
        scrollTo,
      }),
      [emblaApi, scrollState, scrollSnaps, scrollPrev, scrollNext, scrollTo],
    );

    return (
      <CarouselStaticContext.Provider value={staticValue}>
        <CarouselDynamicContext.Provider value={dynamicValue}>
          <div
            ref={ref}
            data-slot="carousel"
            role="region"
            aria-roledescription="carousel"
            tabIndex={0}
            className={cn('relative', className)}
            onKeyDown={handleKeyDown}
            {...props}>
            {children}
          </div>
        </CarouselDynamicContext.Provider>
      </CarouselStaticContext.Provider>
    );
  },
);
Carousel.displayName = 'Carousel';

// ---------------------------------------------------------------------------
// CarouselContent
// ---------------------------------------------------------------------------

export interface CarouselContentProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Classes applied to the overflow-hidden viewport wrapper (e.g. `-mx-4` for bleed) */
  viewportClassName?: string;
}

const CarouselContent = forwardRef<HTMLDivElement, CarouselContentProps>(
  ({ className, viewportClassName, style: userStyle, ...props }, ref) => {
    const { emblaRef, orientation, presetConfig } = useCarouselStatic();
    const useBackfaceHidden = presetConfig?.backfaceHidden !== false;
    return (
      <div
        ref={emblaRef}
        data-slot="carousel-viewport"
        className={cn('overflow-hidden', viewportClassName)}>
        <div
          ref={ref}
          data-slot="carousel-content"
          className={cn(
            'flex',
            orientation === 'vertical' && 'flex-col',
            presetConfig?.classNames?.container,
            className,
          )}
          // User style first so internal touchAction / backfaceVisibility win
          // — required for mobile drag and the sub-pixel-artifact workaround.
          style={{
            ...userStyle,
            touchAction: orientation === 'horizontal' ? 'pan-y pinch-zoom' : 'pan-x pinch-zoom',
            ...(useBackfaceHidden && { backfaceVisibility: 'hidden' }),
          }}
          {...props}
        />
      </div>
    );
  },
);
CarouselContent.displayName = 'CarouselContent';

// ---------------------------------------------------------------------------
// CarouselItem — reads only static context, so it does NOT re-render on scroll.
// ---------------------------------------------------------------------------

export interface CarouselItemProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Render as the child element (Radix Slot pattern). Use when the entire slide
   * should be a link/button — e.g. tap-the-hero patterns where the whole image
   * navigates to a gallery / view-all destination. The consumer's element keeps
   * its natural semantics (slide role attributes are not applied).
   */
  asChild?: boolean;
  children?: React.ReactNode;
}

const CarouselItem = forwardRef<HTMLDivElement, CarouselItemProps>(
  ({ asChild, children, className, ...props }, ref) => {
    const { presetConfig } = useCarouselStatic();
    const Comp = asChild ? Slot : 'div';
    return (
      <Comp
        ref={ref}
        data-slot="carousel-item"
        role={asChild ? undefined : 'group'}
        aria-roledescription={asChild ? undefined : 'slide'}
        className={cn(
          'min-w-0 shrink-0 grow-0 focus-visible:ring-[3px] focus-visible:ring-focus-ring focus-visible:outline-none',
          presetConfig?.classNames?.item || 'basis-full',
          className,
        )}
        {...props}>
        {children}
      </Comp>
    );
  },
);
CarouselItem.displayName = 'CarouselItem';

// ---------------------------------------------------------------------------
// CarouselDots
// ---------------------------------------------------------------------------

export type CarouselDotsVariant = 'inline' | 'overlay' | 'overlay-with-arrows';

export interface CarouselDotsProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Layout variant:
   * - `'inline'` (default): dot pill below the carousel, no chevrons.
   * - `'overlay'`: dot pill anchored bottom-centre, over the carousel.
   * - `'overlay-with-arrows'`: chevron pills at viewport edges flanking the dot pill, overlay.
   */
  variant?: CarouselDotsVariant;
  /** Hide the component when the carousel only has one snap. Defaults to true. */
  hideWhenSingle?: boolean;
  /**
   * Cap the number of dots rendered (Figma "3-5 limit"). When snaps exceed this,
   * the dots window around the active slide instead of rendering one per snap.
   * Defaults to 5.
   */
  maxDots?: number;
}

// Unified white-on-15%-black treatment for all dot/arrow controls — works on any
// background (light, dark, image) without needing contrast-based swapping.
const DOT_ARROW_CLASS =
  'flex size-6 shrink-0 items-center justify-center rounded-full bg-black/15 text-white transition-opacity focus-visible:ring-[3px] focus-visible:ring-focus-ring focus-visible:outline-none';

const CarouselDots = forwardRef<HTMLDivElement, CarouselDotsProps>(
  ({ variant = 'inline', hideWhenSingle = true, maxDots = 5, className, ...props }, ref) => {
    const {
      selectedIndex,
      scrollSnaps,
      scrollTo,
      canScrollPrev,
      canScrollNext,
      scrollPrev,
      scrollNext,
    } = useCarouselDynamic();

    if (hideWhenSingle && scrollSnaps.length <= 1) return null;

    const isOverlay = variant !== 'inline';
    const hasArrows = variant === 'overlay-with-arrows';

    // Cap the dots (Figma "3-5 limit"): large galleries (e.g. a 90-photo venue
    // hero) slide a fixed window of dots around the active slide. The edge dot on
    // any side with more slides beyond the window shrinks to signal the overflow.
    const total = scrollSnaps.length;
    const windowed = total > maxDots;
    const windowStart = windowed
      ? Math.min(Math.max(selectedIndex - Math.floor(maxDots / 2), 0), total - maxDots)
      : 0;
    const visibleDots = Array.from(
      { length: windowed ? maxDots : total },
      (_, i) => windowStart + i,
    );

    return (
      <div
        ref={ref}
        data-slot="carousel-dots"
        className={cn(
          'flex items-center',
          isOverlay ? 'absolute inset-x-0 bottom-3 z-10' : 'py-3',
          // Edge-anchored chevrons (good tap targets) with the dot pill floating
          // centred between via justify-between; otherwise pill is centred.
          hasArrows ? 'justify-between px-3' : 'justify-center',
          className,
        )}
        {...props}>
        {hasArrows && (
          <button
            type="button"
            aria-label="Previous slide"
            disabled={!canScrollPrev}
            onClick={scrollPrev}
            className={cn(DOT_ARROW_CLASS, !canScrollPrev && 'opacity-40')}>
            <ChevronLeft className="size-[18px]" />
          </button>
        )}
        <div className="flex items-center gap-2 rounded-full bg-black/15 p-1">
          <span className="sr-only" aria-live="polite">
            Slide {selectedIndex + 1} of {total}
          </span>
          {visibleDots.map((index) => {
            const shrink =
              windowed &&
              ((index === windowStart && windowStart > 0) ||
                (index === windowStart + maxDots - 1 && windowStart + maxDots < total));
            return (
              <button
                key={index}
                type="button"
                aria-label={`Go to slide ${index + 1} of ${total}`}
                aria-current={index === selectedIndex ? 'step' : undefined}
                className={cn(
                  'size-2 rounded-full transition-all focus-visible:ring-[3px] focus-visible:ring-focus-ring focus-visible:outline-none',
                  index === selectedIndex ? 'bg-white' : 'bg-white/60',
                  shrink && 'size-1.5',
                )}
                onClick={() => scrollTo(index)}
              />
            );
          })}
        </div>
        {hasArrows && (
          <button
            type="button"
            aria-label="Next slide"
            disabled={!canScrollNext}
            onClick={scrollNext}
            className={cn(DOT_ARROW_CLASS, !canScrollNext && 'opacity-40')}>
            <ChevronRight className="size-[18px]" />
          </button>
        )}
      </div>
    );
  },
);
CarouselDots.displayName = 'CarouselDots';

// ---------------------------------------------------------------------------
// CarouselCounter — "N/total" badge for hero-style image carousels
// (Airbnb/Booking pattern). Uses the same white-on-15%-black treatment as
// CarouselDots so it sits over any background.
// ---------------------------------------------------------------------------

export interface CarouselCounterProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Format function — defaults to `${current}/${total}`. */
  format?: (current: number, total: number) => string;
  /**
   * Render as the child element (Radix Slot pattern). Use when the counter
   * should be a link/button — e.g. the venue-profile pattern where tapping
   * the count opens the full photos/videos gallery.
   */
  asChild?: boolean;
  /** Hide the component when the carousel only has one snap. Defaults to true. */
  hideWhenSingle?: boolean;
  children?: React.ReactNode;
}

const CarouselCounter = forwardRef<HTMLDivElement, CarouselCounterProps>(
  ({ asChild, children, className, format, hideWhenSingle = true, ...props }, ref) => {
    const { selectedIndex, scrollSnaps } = useCarouselDynamic();
    if (hideWhenSingle && scrollSnaps.length <= 1) return null;
    const total = scrollSnaps.length;
    const current = selectedIndex + 1;
    const Comp = asChild ? Slot : 'div';
    return (
      <Comp
        ref={ref}
        data-slot="carousel-counter"
        className={cn(
          'inline-flex items-center rounded-full bg-black/15 px-2 py-1 typo-xs text-white transition-opacity hover:opacity-90 focus-visible:ring-[3px] focus-visible:ring-focus-ring focus-visible:outline-none',
          className,
        )}
        {...props}>
        {format ? format(current, total) : `${current}/${total}`}
        <Slottable>{children}</Slottable>
      </Comp>
    );
  },
);
CarouselCounter.displayName = 'CarouselCounter';

// ---------------------------------------------------------------------------
// CarouselPrevious / CarouselNext
// ---------------------------------------------------------------------------

export interface CarouselArrowProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Position the button absolutely over the carousel */
  overlay?: boolean;
}

type CarouselArrowDirection = 'prev' | 'next';

const arrowConfig = {
  prev: {
    icon: ChevronLeft,
    slot: 'carousel-previous',
    label: 'Previous slide',
    overlayPosition: 'left-3',
  },
  next: {
    icon: ChevronRight,
    slot: 'carousel-next',
    label: 'Next slide',
    overlayPosition: 'right-3',
  },
} as const;

function createCarouselArrow(direction: CarouselArrowDirection) {
  const config = arrowConfig[direction];

  const Component = forwardRef<HTMLButtonElement, CarouselArrowProps>(
    (
      { className, overlay = false, onClick: userOnClick, disabled: userDisabled, ...props },
      ref,
    ) => {
      const { scrollPrev, scrollNext, canScrollPrev, canScrollNext } = useCarouselDynamic();
      const scroll = direction === 'prev' ? scrollPrev : scrollNext;
      const canScroll = direction === 'prev' ? canScrollPrev : canScrollNext;

      return (
        <IconButton
          ref={ref}
          data-slot={config.slot}
          variant="outline"
          size="sm"
          icon={config.icon}
          className={cn(
            // Override outline variant's border-space-08 for higher contrast against carousel content
            'border-space-15',
            overlay && `absolute top-1/2 ${config.overlayPosition} z-10 -translate-y-1/2`,
            className,
          )}
          disabled={!canScroll || userDisabled}
          onClick={(e) => {
            userOnClick?.(e);
            if (!e.defaultPrevented) scroll();
          }}
          aria-label={config.label}
          {...props}
        />
      );
    },
  );
  Component.displayName = direction === 'prev' ? 'CarouselPrevious' : 'CarouselNext';
  return Component;
}

const CarouselPrevious = createCarouselArrow('prev');
const CarouselNext = createCarouselArrow('next');

// ---------------------------------------------------------------------------
// CarouselCopy / CarouselCopyTitle — text block rendered below a slide's card.
// Mirrors the Figma "Carousel Copy" primitive. Layout shell and title are
// separate primitives so consumers control structure (matches aura's
// Drawer / DrawerTitle pattern).
// ---------------------------------------------------------------------------

const CarouselCopy = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <Flex
      ref={ref}
      data-slot="carousel-copy"
      className={cn('w-full flex-col gap-1', className)}
      {...props}
    />
  ),
);
CarouselCopy.displayName = 'CarouselCopy';

interface CarouselCopyTitleProps {
  children: React.ReactNode;
  className?: string;
}

const CarouselCopyTitle = ({ children, className }: CarouselCopyTitleProps) => (
  <Text
    data-slot="carousel-copy-title"
    variant="typo-sm-semibold"
    className={cn('truncate', className)}>
    {children}
  </Text>
);
CarouselCopyTitle.displayName = 'CarouselCopyTitle';

// ---------------------------------------------------------------------------
// CarouselHeader
// ---------------------------------------------------------------------------

export interface CarouselHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Section title */
  title?: React.ReactNode;
  /** "View all" link target */
  href?: string;
  /** "View all" click handler (alternative to href) */
  onViewAll?: () => void;
  /** Label for the view-all action */
  viewAllLabel?: string;
  /** Show prev/next arrows in the header (defaults to true, hidden on mobile) */
  showArrows?: boolean;
  /** Custom action element (replaces default "View all" button) */
  action?: React.ReactNode;
}

const CarouselHeader = forwardRef<HTMLDivElement, CarouselHeaderProps>(
  (
    {
      title,
      href,
      onViewAll,
      viewAllLabel = 'View all',
      showArrows = true,
      action,
      className,
      ...props
    },
    ref,
  ) => {
    function renderViewAll(): React.ReactNode {
      if (action) return action;
      if (href) {
        return (
          <Button variant="ghost" size="sm" asChild>
            <a href={href}>{viewAllLabel}</a>
          </Button>
        );
      }
      if (onViewAll) {
        return (
          <Button variant="ghost" size="sm" onClick={onViewAll}>
            {viewAllLabel}
          </Button>
        );
      }
      return null;
    }

    const viewAllElement = renderViewAll();

    return (
      <div
        ref={ref}
        data-slot="carousel-header"
        className={cn('flex items-center justify-between pb-3', className)}
        {...props}>
        {title &&
          (typeof title === 'string' ? <Text variant="typo-base-semibold">{title}</Text> : title)}
        <div className="flex items-center gap-2">
          {viewAllElement}
          {showArrows && (
            <div className="hidden items-center gap-2 md:flex">
              <CarouselPrevious />
              <CarouselNext />
            </div>
          )}
        </div>
      </div>
    );
  },
);
CarouselHeader.displayName = 'CarouselHeader';

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselDots,
  CarouselCounter,
  CarouselPrevious,
  CarouselNext,
  CarouselHeader,
  CarouselCopy,
  CarouselCopyTitle,
};
